import { css } from "@emotion/css";
import { Collapse } from "@octopusdeploy/design-system-components";
import { ChevronDownIcon, ChevronRightIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontSize, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ToolCallEvent, ToolResponseEvent } from "@octopusdeploy/octopus-server-client";
import React, { useState } from "react";
type ToolCallDisplayProps = {
    toolCall: ToolCallEvent;
    toolResponse?: ToolResponseEvent;
};
const getToolCallDisplayName = (toolCall: ToolCallEvent): string => {
    // Hard-coded mapping for GetServerTaskRaw tool
    if (toolCall.functionName === "GetServerTaskRaw") {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const args = toolCall.arguments as {
            retrievalMode?: string;
        };
        const mode = args.retrievalMode || "SmartSummary";
        switch (mode) {
            case "SmartSummary":
                return "Analyzing deployment logs";
            case "Search":
                return "Searching deployment logs";
            case "LineRange":
                return "Reading specific log lines";
            case "Tail":
                return "Reading recent log entries";
            case "LogLevel":
                return "Filtering logs by severity";
            default:
                return "Retrieving deployment logs";
        }
    }
    // Default: show pluginName.functionName
    return `${toolCall.pluginName}.${toolCall.functionName}`;
};
export const ToolCallDisplay = ({ toolCall, toolResponse }: ToolCallDisplayProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpanded = () => setIsExpanded((prev) => !prev);
    return (<div className={styles.container}>
            <div className={styles.header} onClick={toggleExpanded} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && toggleExpanded()}>
                <div className={styles.headerContent}>
                    {isExpanded ? <ChevronDownIcon size={16} color={themeTokens.color.icon.secondary}/> : <ChevronRightIcon size={16} color={themeTokens.color.icon.secondary}/>}
                    <span className={styles.functionName}>{getToolCallDisplayName(toolCall)}</span>
                </div>
            </div>

            <Collapse in={isExpanded}>
                <div className={styles.content}>
                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>Arguments</div>
                        <pre className={styles.codeBlock}>{JSON.stringify(toolCall.arguments, null, 2)}</pre>
                    </div>

                    {toolResponse && (<div className={styles.section}>
                            <div className={styles.sectionTitle}>Response</div>
                            <div className={styles.responseContent}>
                                <pre className={styles.codeBlock}>{formatResponse(toolResponse.response)}</pre>
                            </div>
                        </div>)}

                    {!toolResponse && (<div className={styles.section}>
                            <div className={styles.loadingText}>Executing tool...</div>
                        </div>)}
                </div>
            </Collapse>
        </div>);
};
function formatResponse(input: string): string {
    let result = input;
    try {
        // Try to parse as JSON for better formatting
        const parsed = JSON.parse(input);
        result = JSON.stringify(parsed, null, 2);
    }
    catch {
        // If not JSON, return as is (truncated if too long)
        if (input.length > 500) {
            result = `${input.substring(0, 500)}
... (truncated)`;
        }
    }
    return result.replace(/\\r\\n/g, "\n").replace(/\\n/g, "\n");
}
const styles = {
    container: css({
        marginBottom: space[8],
    }),
    header: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `${space[6]} 0`,
        cursor: "pointer",
        userSelect: "none",
        transition: "opacity 0.2s",
        "&:hover": {
            opacity: 0.8,
        },
    }),
    headerContent: css({
        display: "flex",
        alignItems: "center",
        gap: space[8],
        flex: 1,
        minWidth: 0,
        "@media (max-width: 1024px)": {
            gap: space[6],
        },
    }),
    functionName: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
        fontFamily: "monospace",
        flex: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        "@media (max-width: 1024px)": {
            fontSize: fontSize.xSmall,
        },
    }),
    content: css({
        paddingLeft: space[24],
        paddingBottom: space[8],
        "@media (max-width: 1024px)": {
            paddingLeft: space[16],
            paddingBottom: space[6],
        },
    }),
    section: css({
        marginTop: space[12],
        "&:first-of-type": {
            marginTop: 0,
        },
        "@media (max-width: 1024px)": {
            marginTop: space[8],
        },
    }),
    sectionTitle: css({
        font: text.body.bold.small,
        color: themeTokens.color.text.secondary,
        marginBottom: space[6],
        "@media (max-width: 1024px)": {
            fontSize: fontSize.small,
            marginBottom: space[4],
        },
    }),
    codeBlock: css({
        backgroundColor: themeTokens.color.background.secondary.default,
        padding: space[12],
        borderRadius: borderRadius.small,
        fontSize: fontSize.small,
        fontFamily: "monospace",
        overflow: "auto",
        maxHeight: "300px",
        margin: 0,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        "@media (max-width: 1024px)": {
            padding: space[8],
            fontSize: fontSize.xSmall,
            maxHeight: "200px",
        },
    }),
    responseContent: css({
        position: "relative",
    }),
    loadingText: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
        fontStyle: "italic",
        padding: space[8],
        "@media (max-width: 1024px)": {
            fontSize: fontSize.small,
            padding: space[6],
        },
    }),
};
